import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Filter, ImageIcon, MessageSquare, ChevronRight, Shield, AlertTriangle, Lock } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import MembersTab from "./MembersTab";
import PushMessagesTab from "./PushMessagesTab";
import OwnerOverview from "./OwnerOverview";

const STATUSES = [
  { key: "", label: "all" },
  { key: "pending", label: "pending", color: "#FFB000" },
  { key: "in_progress", label: "in progress", color: "#3FA7FF" },
  { key: "completed", label: "completed", color: "#00FF66" },
];

const TICKET_STATUSES = [
  { key: "", label: "all" },
  { key: "open", label: "open", color: "#00E5FF" },
  { key: "pending", label: "pending", color: "#FFB000" },
  { key: "closed", label: "closed", color: "#888" },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("reviews"); // reviews | tickets
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [defaultAdmin, setDefaultAdmin] = useState(null); // {email, exists, disabled, role}
  const [disabling, setDisabling] = useState(false);

  const loadDefaultStatus = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/admins/default-status");
      setDefaultAdmin(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadDefaultStatus(); }, [loadDefaultStatus]);

  const isSelfDefault = user && defaultAdmin && user.email?.toLowerCase() === defaultAdmin.email?.toLowerCase();

  const load = useCallback(async () => {
    if (tab !== "reviews" && tab !== "tickets") {
      // Still refresh stats for the header cards
      try {
        const { data: s } = await http.get("/admin/stats");
        setStats(s);
      } catch { /* ignore */ }
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const sParams = new URLSearchParams();
      if (statusFilter) sParams.set("status", statusFilter);
      if (search.trim()) sParams.set("search", search.trim());
      const endpoint = tab === "reviews" ? "/admin/reviews" : "/admin/tickets";
      const [{ data: list }, { data: s }] = await Promise.all([
        http.get(`${endpoint}?${sParams.toString()}`),
        http.get("/admin/stats"),
      ]);
      setItems(list || []);
      setStats(s);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [tab, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const disableDefault = async () => {
    if (!window.confirm(
      `Disable default admin account "${defaultAdmin?.email}"?\n\n` +
      "This will demote its role to 'user', rotate its password to an unrecoverable value, " +
      "and mark it disabled. The account will no longer be able to log in.\n\nProceed?"
    )) return;
    setDisabling(true);
    try {
      await http.post("/admin/admins/disable-default");
      toast.success("Default admin disabled");
      await loadDefaultStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setDisabling(false); }
  };

  return (
    <section className="pt-10 pb-20" data-testid="admin-dashboard">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFB000] mb-3">
          <Shield className="w-3.5 h-3.5" /> ── admin console ──
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-white">
          Operations
        </h1>
        <p className="text-sm text-neutral-400 mt-1.5">Manage Expert Reviews and follow-up tickets.</p>

        {/* Owner Overview — KPI cards (auto-pulled from /api/admin/overview) */}
        <OwnerOverview />

        {/* Quick filter stats for the active tab */}
        {stats && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
            <Stat label="pending" value={stats.reviews_pending} color="#FFB000" />
            <Stat label="in progress" value={stats.reviews_in_progress} color="#3FA7FF" />
            <Stat label="completed" value={stats.reviews_completed} color="#00FF66" />
            <Stat label="tickets open" value={stats.tickets_open} color="#00E5FF" />
          </div>
        )}

        {/* Tabs */}
        <div className="mt-8 flex items-center gap-1 border-b border-neutral-900 overflow-x-auto">
          <TabBtn active={tab === "reviews"} onClick={() => { setTab("reviews"); setStatusFilter(""); }} testid="tab-reviews">
            Expert Reviews
          </TabBtn>
          <TabBtn active={tab === "tickets"} onClick={() => { setTab("tickets"); setStatusFilter(""); }} testid="tab-tickets">
            Tickets
          </TabBtn>
          <TabBtn active={tab === "members"} onClick={() => setTab("members")} testid="tab-members">
            Members
          </TabBtn>
          <TabBtn active={tab === "push"} onClick={() => setTab("push")} testid="tab-push">
            Push Messages
          </TabBtn>
        </div>

        {tab === "members" && <MembersTab />}
        {tab === "push" && <PushMessagesTab />}

        {(tab === "reviews" || tab === "tickets") && (<>
        {/* Filters */}
        <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="admin-filters">
          <div className="flex items-center gap-2 border border-neutral-800 bg-neutral-950 px-3 py-2 flex-1 min-w-[220px]">
            <Search className="w-3.5 h-3.5 text-neutral-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === "reviews" ? "Search email, asset or name…" : "Search email or asset…"}
              data-testid="admin-search"
              className="flex-1 bg-transparent text-sm text-white placeholder-neutral-600 outline-none"
            />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-neutral-500 mr-1" />
            {(tab === "reviews" ? STATUSES : TICKET_STATUSES).map((s) => (
              <button
                key={s.key}
                onClick={() => setStatusFilter(s.key)}
                data-testid={`admin-filter-${s.key || "all"}`}
                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1.5 border ${
                  statusFilter === s.key
                    ? "border-white text-white"
                    : "border-neutral-800 text-neutral-400 hover:text-white hover:border-neutral-600"
                }`}
                style={statusFilter === s.key && s.color ? { color: s.color, borderColor: s.color } : {}}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="mt-5 space-y-2" data-testid="admin-list">
          {loading && (
            <div className="text-xs font-mono text-neutral-600 uppercase tracking-[0.25em] py-8">loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="text-xs font-mono text-neutral-600 uppercase tracking-[0.25em] py-8">no items</div>
          )}
          {tab === "reviews"
            ? items.map((r) => <ReviewRow key={r.id} r={r} />)
            : items.map((t) => <TicketRow key={t.id} t={t} />)}
        </div>
        </>)}

        {/* Security panel: disable seeded default admin */}
        {defaultAdmin?.exists && (
          <div className="mt-12 border-t border-neutral-900 pt-8" data-testid="admin-security-panel">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFB000] mb-3">
              <Lock className="w-3.5 h-3.5" /> ── security ──
            </div>
            <h2 className="text-xl font-black tracking-tighter text-white">Default admin account</h2>
            <p className="text-xs text-neutral-400 mt-1.5 max-w-2xl">
              The seeded development admin (<span className="font-mono text-neutral-300">{defaultAdmin.email}</span>) is
              {defaultAdmin.disabled ? " " : " still "}
              <span style={{ color: defaultAdmin.disabled ? "#888" : "#FFB000" }}>
                {defaultAdmin.disabled ? "disabled" : "active"}
              </span>.
              {!defaultAdmin.disabled && " For production, disable it once you've verified your own admin account works."}
            </p>

            {!defaultAdmin.disabled && (
              <div className="mt-4 border border-[#FFB000]/30 bg-[#FFB000]/[0.04] p-4 max-w-2xl" data-testid="default-admin-card">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 mt-0.5 text-[#FFB000] flex-shrink-0" />
                  <div className="flex-1 text-xs text-neutral-300 leading-relaxed">
                    <p>Clicking the button below will permanently:</p>
                    <ul className="mt-2 ml-3 space-y-1 list-disc">
                      <li>Set role <span className="font-mono">admin → user</span></li>
                      <li>Rotate password to an unrecoverable random value</li>
                      <li>Mark account <span className="font-mono">disabled: true</span></li>
                      <li>Invalidate any active sessions for the account</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <button
                    onClick={disableDefault}
                    disabled={disabling || isSelfDefault}
                    data-testid="disable-default-admin-btn"
                    className="inline-flex items-center gap-1.5 bg-[#FFB000] hover:bg-[#FFA500] disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold text-xs uppercase tracking-[0.15em] px-4 py-2 transition-colors"
                  >
                    <Lock className="w-3.5 h-3.5" />
                    {disabling ? "Disabling…" : "Disable default admin"}
                  </button>
                  {isSelfDefault && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-red-400">
                      can't disable yourself — sign in as another admin first
                    </span>
                  )}
                </div>
              </div>
            )}

            {defaultAdmin.disabled && (
              <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-neutral-500 font-mono uppercase tracking-[0.2em]" data-testid="default-admin-disabled-state">
                <Lock className="w-3.5 h-3.5" /> default admin disabled
              </div>
            )}
          </div>
        )}
      </motion.div>
    </section>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="border border-neutral-900 bg-neutral-950 px-4 py-3">
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">{label}</div>
      <div className="font-black text-2xl mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2.5 text-xs font-mono uppercase tracking-[0.25em] border-b-2 transition-colors ${
        active ? "border-[#00E5FF] text-white" : "border-transparent text-neutral-500 hover:text-white"
      }`}
    >{children}</button>
  );
}

function ReviewRow({ r }) {
  const color = r.status === "completed" ? "#00FF66" : r.status === "in_progress" ? "#3FA7FF" : "#FFB000";
  return (
    <Link
      to={`/admin/reviews/${r.id}`}
      data-testid={`admin-review-row-${r.id}`}
      className="block border border-neutral-900 hover:border-neutral-700 bg-neutral-950 p-4 transition-colors"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-bold text-white">{r.asset}</span>
            <span className="text-xs text-neutral-500">·</span>
            <span className="text-xs text-neutral-400 truncate">{r.user_email || "—"}</span>
            {r.has_image && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.2em] text-[#00E5FF]">
                <ImageIcon className="w-3 h-3" /> img
              </span>
            )}
            {r.has_open_ticket && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.2em] text-[#FFB000]">
                <MessageSquare className="w-3 h-3" /> ticket
              </span>
            )}
          </div>
          {r.question && (
            <div className="text-xs text-neutral-500 mt-1 truncate">{r.question}</div>
          )}
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-700 mt-1">
            {new Date(r.created_at).toLocaleString()}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color }}>{r.status}</span>
          <ChevronRight className="w-4 h-4 text-neutral-600" />
        </div>
      </div>
    </Link>
  );
}

function TicketRow({ t }) {
  const color = t.status === "open" ? "#00E5FF" : t.status === "pending" ? "#FFB000" : "#888";
  const last = (t.messages || []).slice(-1)[0];
  return (
    <Link
      to={`/admin/reviews/${t.review_id}`}
      data-testid={`admin-ticket-row-${t.id}`}
      className="block border border-neutral-900 hover:border-neutral-700 bg-neutral-950 p-4 transition-colors"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-bold text-white">{t.asset || "—"}</span>
            <span className="text-xs text-neutral-500">·</span>
            <span className="text-xs text-neutral-400 truncate">{t.user_email || "—"}</span>
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-600">
              {(t.messages || []).length} msg
            </span>
          </div>
          {last && (
            <div className="text-xs text-neutral-500 mt-1 truncate">
              {last.author === "admin" ? "you: " : "user: "}{last.body}
            </div>
          )}
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-700 mt-1">
            {new Date(t.updated_at).toLocaleString()}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color }}>{t.status}</span>
          <ChevronRight className="w-4 h-4 text-neutral-600" />
        </div>
      </div>
    </Link>
  );
}
